import '../../styles/InteractiveComponents.css';

import { IconButton, Paper } from '@mui/material';
import React, { useState } from 'react';
import { cloneDeep, isNull } from 'lodash';

import { ApiPortfolioState } from '../../../dashboard/models/ApiPortfolioState';
import AssetLibrary from '../../../shared/components/AssetLibrary/AssetLibrary';
import AssetService from '../../../shared/services/AssetService';
import CancelIcon from '../../images/cancel.svg';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import { DraggableProvided } from 'react-beautiful-dnd';
import Grid from '@mui/material/Unstable_Grid2';
import ImageIcon from '@mui/icons-material/Image';
import { InlineTextEdit } from '../InlineTextEdit';
import { SequencerComponent } from '../../models/LessonComponent';
import { SequencerItem } from '../../models/LessonComponentContent';
import TickIcon from '../../images/tick.svg';
import styles from '../../styles/Sequencer.module.css';
import { useLessonComponentContext } from '../../../content/models/LessonComponentContext';
import { useLessonContext } from '../../models/LessonContext';
import { useLessonPlayerContext } from '../../../dashboard/models/LessonPlayerContext';

interface SequencerProps {
  item: SequencerItem;
  component: SequencerComponent;
  provided2?: DraggableProvided;
  index?: number;
  onItemChange: (item: SequencerItem) => void;
}

export const SequencerSelectionItem = (props: SequencerProps) => {
  const { mode, lessonType, finalized } = useLessonContext();
  const { reattempt, correct, isAnswerModified, isSurvey } =
    useLessonComponentContext();
  const { lessonState } = useLessonPlayerContext();
  const [showAssetLibrary, setShowAssetLibrary] = useState(false);

  const item = props.item;
  const imageUrl = item.image && AssetService.getAssetUrl(item.image);

  const isCorrectAnswer = props.component.content.correctResponsePattern
    ? JSON.parse(props.component.content.correctResponsePattern)[
        props.index
      ] === item.id
    : null;

  const handleAssetSelect = (assetkey: string) => {
    const copy = cloneDeep(item);
    copy.image = assetkey;
    props.onItemChange(copy);
  };

  /** Update the text label for a single item. */
  const onItemLabelChange = (label: string) => {
    const copy = cloneDeep(item);
    copy.label = label;
    props.onItemChange(copy);
  };

  /**
   * Returns a value indicating whether to show icons that indicate whether each
   * answer is correct or incorrect, depending on the lesson type, lesson state
   * and mode.
   */
  const showMarkingIcon = () => {
    if (
      // No icons until the user has answered. By default "correct" value would be null.
      isNull(correct) ||
      // Hide icons when "Reattempt" is true AND the lesson is not finalized.
      (reattempt && !finalized)
    ) {
      return false;
    }

    return (
      // Show icons for basic lesson type.
      lessonType === 'lesson' ||
      // Show icons when learner finalized quiz answers.
      (lessonType === 'quiz' && finalized) ||
      // Show the icons after the marker finalized the lesson assessment.
      lessonState?.state === ApiPortfolioState.AssessmentLock ||
      // Check Assessment status is "Work Requested" and user has not modified the
      // answers then show the icons.
      (lessonState?.state === ApiPortfolioState.AssessmentRetry &&
        !isAnswerModified) ||
      // Always show the icons for marker.
      mode === 'mark'
    );
  };

  return (
    <>
      <Paper
        elevation={2}
        className={styles.paperProperty}
        style={{ backgroundImage: `url(${imageUrl})` }}
        {...(mode !== 'edit' && { ...props.provided2?.dragHandleProps })}
      >
        <Grid
          container
          direction="row"
          alignItems="center"
          className={`${styles.paperBackground} ${
            item.image
              ? styles.paperImageBackground
              : styles.paperDefaultBackground
          }`}
        >
          {mode !== 'mark' && (
            <Grid
              {...props.provided2?.dragHandleProps}
              style={{
                width: '28px', // Make both leftside and rightside Grid with same width
                color: item.image ? 'white' : 'rgba(0,0,0,0.54)' // to make it look the same as the 'delete' icon button
              }}
            >
              <DragIndicatorIcon />
            </Grid>
          )}

          <Grid
            className={`${styles.gridStretch} ${
              mode === 'edit'
                ? styles.itemTextAlignAuthor
                : styles.itemTextAlignLearner
            }`}
          >
            {mode === 'edit' ? (
              <InlineTextEdit
                value={item?.label || ''}
                placeholder={
                  !item.id ? 'Click to add an item' : 'Click to edit item label'
                }
                onChange={(label) => onItemLabelChange(label)}
                className={item.image ? styles.imageText : styles.text}
              />
            ) : (
              <span className={item.image ? styles.imageText : styles.text}>
                {item.label || ''}
              </span>
            )}
          </Grid>

          {mode === 'edit' && (
            <Grid>
              {item.image ? (
                <img
                  className={styles.sequencerItemImageThumbnail}
                  src={imageUrl}
                  onClick={() => setShowAssetLibrary(true)}
                />
              ) : (
                <IconButton
                  className={styles.sequencerItemThumbnail}
                  size="small"
                  onClick={() => setShowAssetLibrary(true)}
                >
                  <ImageIcon />
                </IconButton>
              )}
            </Grid>
          )}
          {mode !== 'edit' && !isSurvey && (
            // Make both leftside and rightside Grid with same width
            <Grid style={{ width: '28px' }}>
              {showMarkingIcon() && (
                <Grid className={styles.sequencerItemMarking}>
                  {isCorrectAnswer ? (
                    <img src={TickIcon} alt="correct" />
                  ) : (
                    <img src={CancelIcon} alt="wrong" />
                  )}
                </Grid>
              )}
            </Grid>
          )}
        </Grid>
      </Paper>
      {showAssetLibrary && (
        <AssetLibrary
          types={['image']}
          category="author"
          title="Select Image"
          selectedAssetkey={item.image}
          onSelect={(assetkey) => handleAssetSelect(assetkey)}
          onClose={() => setShowAssetLibrary(false)}
        />
      )}
    </>
  );
};
